import ListSearchBar from "../components/ListSearchBar";
/* eslint-disable eqeqeq */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { Table } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";
import ReactPaginate from "react-paginate";
import { useTranslate } from "../hooks/LanguageProvider";
import { PropagateLoader } from "react-spinners";
import { toast } from "react-toastify";
import { removeHiddenColumn } from "../utilities/functions";
import { BASE_MAP_OPTIONS, BASE_MAP_TOOL_OPTIONS } from "../config/maps/baseMaps";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

interface PreferenceRow {
  id_user: number;
  id_page: number;
  column_name: string;
  updated_at: string;
  user_name?: string;
  page_name?: string;
}

type PageGroup = {
  id_page: number;
  page_name?: string;
  columns: string[];
  updated_at: string; // latest for this page
};

type UserGroup = {
  id_user: number;
  user_name?: string;
  pages: PageGroup[];
  updated_at: string; // latest across pages
};

type FilterKey = "user_name" | "page_name" | "column_name" | "updated_at";


function formatDateFR(v?: string | null) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);

  return d.toLocaleString("fr-FR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}


/** ✅ Group DB rows => one block per user; inside: pages */
function groupPreferencesByUser(rows: PreferenceRow[]): UserGroup[] {
  const users = new Map<number, { id_user: number; user_name?: string; pages: Map<number, PageGroup> }>();

  for (const r of rows) {
    if (!users.has(r.id_user)) {
      users.set(r.id_user, { id_user: r.id_user, user_name: r.user_name, pages: new Map() });
    }
    const u = users.get(r.id_user)!;

    if (!u.pages.has(r.id_page)) {
      u.pages.set(r.id_page, {
        id_page: r.id_page,
        page_name: r.page_name,
        columns: [r.column_name],
        updated_at: r.updated_at,
      });
    } else {
      const p = u.pages.get(r.id_page)!;
      if (!p.columns.includes(r.column_name)) p.columns.push(r.column_name);
      if (new Date(r.updated_at).getTime() > new Date(p.updated_at).getTime()) {
        p.updated_at = r.updated_at;
      }
    }
  }

  const result: UserGroup[] = Array.from(users.values()).map((u) => {
    const pages = Array.from(u.pages.values());
    pages.forEach((p) => p.columns.sort());

    // user updated_at = latest across pages
    const userLatest =
      pages.reduce((max, p) => {
        const t = new Date(p.updated_at).getTime();
        return Math.max(max, t);
      }, 0) || 0;

    // default sort pages by latest desc
    pages.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

    return {
      id_user: u.id_user,
      user_name: u.user_name,
      pages,
      updated_at: userLatest ? new Date(userLatest).toISOString() : "",
    };
  });

  // default sort users by latest desc
  result.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  return result;
}

export function Preferences() {
  const { translate } = useTranslate();
  const id_user = Number(localStorage.getItem("userid") ?? 0);

  const [usersAll, setUsersAll] = useState<UserGroup[]>([]);
  const [usersPage, setUsersPage] = useState<UserGroup[]>([]);

  const [pageCount, setPageCount] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPagesRows, setTotalPagesRows] = useState(0);

  const [limitValue, setSelectedLimitValue] = useState(10);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [search, setSearch] = useState("");
  const [column, setSortColumn] = useState<FilterKey>("updated_at");
  const [sort, setSort] = useState<"ASC" | "DESC">("DESC");

  const [filter, setFilter] = useState<FilterKey>("user_name");
  const [selectedSearch, setSelectedSearch] = useState<string>(translate("User"));

  // ✅ Your forced page labels (avoid backend names like "Roles and permissions")
  const PAGE_MAP: Record<number, string> = useMemo(
    () => ({
      3: translate("Map"),
      4: translate("Fleet view"),
      6: translate("Vehicles"),
      2: translate("Roles"),
      86: translate("Sites"),
      16: translate("Points d'Intérêt"),
    }),
    [translate]
  );

  // ✅ Column labels per page (keys MUST match DB column_name)
  const PAGE_COLUMNS_LABELS: Record<number, Record<string, string>> = useMemo(
    () => ({
      4: {
        immatriculation_vehicule: translate("Matriculation"),
        psn_dispositif: translate("PSN"),
        nom_groupe: translate("Vehicles Groups"),
        nom_user: translate("User"),
        nom_conducteur: translate("Driver"),
        TIMESTAMP: translate("Date Time"),
        GPSDIST: translate("Distance"),
        GSMLVL: translate("GSM"),
        SOG: translate("Speed"),
      },

      6: {
       id_vehicule: translate("ID"),
  vehicule_type: translate("Type"),
  immatriculation_vehicule: translate("Immatriculation"),
  PSN: translate("PSN"),
  username_user: translate("Utilisateur"),
  nom_groupe: translate("Groupe"),
  GPSDIST: translate("Odomètre"),
  vehicle_door_number: translate("N° porte"),
  
      },

      2: {
        id_role: "ID",
        nom_role: translate("Nom"),
        username_user: translate("Utilisateur"),
        date_creation_role: translate("Date de création"),
      },

      86: {
        id_site: translate("ID Site"),
        site: translate("Site"),
      },

      16: {
        idgeof: "ID POI",
        codegeof: translate("Code"),
        namegeof: translate("Nom"),
        descriptiongeof: translate("Description"),
        areageof: translate("Type"),
        username_user: translate("Utilisateur"),
        etatgeof: translate("Etat"),
        datecreategeof: translate("Date de création"),
      },
    }),
    [translate]
  );

  const getColumnLabel = (id_page: number, column_name: string) => {
    if (column_name.startsWith("base_map:")) {
      const [, toolKeyOrValue, value] = column_name.split(":");
      const baseMapValue = value || toolKeyOrValue;
      const toolLabel = value
        ? BASE_MAP_TOOL_OPTIONS.find((tool) => tool.key === toolKeyOrValue)
          ?.label
        : "";
      const baseMapLabel =
        BASE_MAP_OPTIONS.find((option) => option.value === baseMapValue)
          ?.label ?? baseMapValue;
      return toolLabel
        ? `${translate("Fond de carte")} ${translate(toolLabel)}: ${baseMapLabel}`
        : `${translate("Fond de carte")}: ${baseMapLabel}`;
    }

    return PAGE_COLUMNS_LABELS[id_page]?.[column_name] ?? column_name;
  };

  const getPageLabel = (id_page: number, backendName?: string) =>
    PAGE_MAP[id_page] ?? backendName ?? String(id_page);

  const getColumnsDisplay = (id_page: number, cols: string[]) =>
    cols.map((c) => getColumnLabel(id_page, c)).join(", ");

  // ✅ Filter users + optionally filter pages inside each user
  const applySearch = (users: UserGroup[], q: string, f: FilterKey): UserGroup[] => {
    const s = q.trim().toLowerCase();
    if (!s) return users;

    return users
      .map((u) => {
        const userVal = (u.user_name ?? String(u.id_user)).toLowerCase();

        // user filter
        if (f === "user_name") {
          return userVal.includes(s) ? u : null;
        }

        // page filter => keep only matching pages
        const pagesFiltered = u.pages.filter((p) => {
          const pageVal = getPageLabel(p.id_page, p.page_name).toLowerCase();
          const colsVal = getColumnsDisplay(p.id_page, p.columns).toLowerCase();
          const dateVal = (p.updated_at ?? "").toLowerCase();

          if (f === "page_name") return pageVal.includes(s);
          if (f === "column_name") return colsVal.includes(s);
          if (f === "updated_at") return dateVal.includes(s);
          return false;
        });

        if (pagesFiltered.length === 0) return null;

        // rebuild user with only matched pages, and updated_at recomputed
        const latest =
          pagesFiltered.reduce((max, p) => Math.max(max, new Date(p.updated_at).getTime()), 0) || 0;

        return {
          ...u,
          pages: pagesFiltered,
          updated_at: latest ? new Date(latest).toISOString() : u.updated_at,
        };
      })
      .filter(Boolean) as UserGroup[];
  };

  // ✅ Sort users (global)
  const sortUsers = (users: UserGroup[], col: FilterKey, dir: "ASC" | "DESC") => {
    const factor = dir === "ASC" ? 1 : -1;

    return [...users].sort((a, b) => {
      if (col === "updated_at") {
        return (new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()) * factor;
      }
      if (col === "user_name") {
        const au = (a.user_name ?? String(a.id_user)).toLowerCase();
        const bu = (b.user_name ?? String(b.id_user)).toLowerCase();
        return au.localeCompare(bu) * factor;
      }
      if (col === "page_name") {
        const ap = getPageLabel(a.pages[0]?.id_page ?? 0, a.pages[0]?.page_name).toLowerCase();
        const bp = getPageLabel(b.pages[0]?.id_page ?? 0, b.pages[0]?.page_name).toLowerCase();
        return ap.localeCompare(bp) * factor;
      }
      if (col === "column_name") {
        const ac = a.pages[0] ? getColumnsDisplay(a.pages[0].id_page, a.pages[0].columns).toLowerCase() : "";
        const bc = b.pages[0] ? getColumnsDisplay(b.pages[0].id_page, b.pages[0].columns).toLowerCase() : "";
        return ac.localeCompare(bc) * factor;
      }
      return 0;
    });
  };

  const slicePage = (users: UserGroup[], page: number, limit: number) => {
    const start = (page - 1) * limit;
    const end = start + limit;
    return users.slice(start, end);
  };

  const refreshPagedView = (allUsers: UserGroup[], nextPage: number, nextLimit: number) => {
    const filtered = applySearch(allUsers, search, filter);
    const sorted = sortUsers(filtered, column, sort);

    setTotalUsers(sorted.length);

    // total "page rows" across filtered users (for title)
    const pagesCount = sorted.reduce((sum, u) => sum + u.pages.length, 0);
    setTotalPagesRows(pagesCount);

    const pc = Math.ceil(sorted.length / nextLimit) || 1;
    setPageCount(pc);

    const safePage = Math.max(1, Math.min(nextPage, pc));
    setCurrentPage(safePage);

    setUsersPage(slicePage(sorted, safePage, nextLimit));
  };

  const getPreferences = async () => {
    const FETCH_LIMIT = 5000;

    const bodyData = JSON.stringify({
      limitValue: FETCH_LIMIT,
      currentPage: 1,
      search: "",
      column: "updated_at",
      sort: "DESC",
      filter: "updated_at",
      id_user,
    });

    try {
      setLoading(true);

      const res = await fetch(`${backendUrl}/api/preference/list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: bodyData,
        mode: "cors",
      });

      if (!res.ok) {
        setUsersAll([]);
        setUsersPage([]);
        setTotalUsers(0);
        setTotalPagesRows(0);
        setPageCount(0);
        return;
      }

      const data: PreferenceRow[] = await res.json();
      const groupedUsers = groupPreferencesByUser(data);

      setUsersAll(groupedUsers);
      refreshPagedView(groupedUsers, currentPage, limitValue);
    } catch (e) {
      console.error(e);
      setUsersAll([]);
      setUsersPage([]);
      setTotalUsers(0);
      setTotalPagesRows(0);
      setPageCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshPagedView(usersAll, currentPage, limitValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersAll, search, filter, column, sort, currentPage, limitValue]);

  const OPTIONS: { key: FilterKey; label: string }[] = [
    { key: "user_name", label: translate("User") },
    { key: "page_name", label: translate("Page") },
    { key: "column_name", label: translate("Column") },
    { key: "updated_at", label: translate("Last update") },
  ];

  const filterLabel =
    filter === "user_name"
      ? translate("User")
      : filter === "page_name"
      ? translate("Page")
      : filter === "column_name"
      ? translate("Column")
      : translate("Last update");

  const handleTypeSearch = (event: any) => {
    const selectedValue = event.target.textContent;

    if (selectedValue === translate("User")) setFilter("user_name");
    if (selectedValue === translate("Page")) setFilter("page_name");
    if (selectedValue === translate("Column")) setFilter("column_name");
    if (selectedValue === translate("Last update")) setFilter("updated_at");

    setSelectedSearch(selectedValue);
    setCurrentPage(1);
  };

  const handleAdvancedSearch = (event: any) => {
    setSearch(event.target.value);
    setCurrentPage(1);
  };

  const handleSelectChange = (event: any) => {
    setSelectedLimitValue(parseInt(event.target.value, 10));
    setCurrentPage(1);
    window.scrollTo(0, 0);
  };

  const handlePageClick = (data: any) => {
    setCurrentPage(data.selected + 1);
    window.scrollTo(0, 0);
  };

  const handleSortingColum = (currentColumn: FilterKey) => {
    const nextSort = sort === "DESC" ? "ASC" : "DESC";
    setSort(nextSort);
    setSortColumn(currentColumn);
    setCurrentPage(1);
  };

  // ✅ UNHIDE single column (remove row from preferences_columns)
  const handleUnhide = async (userId: number, pageId: number, colKey: string) => {
    const label = getColumnLabel(pageId, colKey);
    const ok = window.confirm(`${translate("Show")} "${label}" ?`);
    if (!ok) return;

    try {
      await removeHiddenColumn({ id_user: userId, id_page: pageId, column_name: colKey });
      toast.success(translate("Saved"), { position: "bottom-right", autoClose: 1500 });
      await getPreferences();
    } catch (e) {
      console.error(e);
      toast.error(translate("Save failed"), { position: "bottom-right", autoClose: 2500 });
    }
  };

  // ✅ Delete page line: unhide all columns of that page (so it disappears from preferences)
  const handleDeletePageLine = async (userId: number, page: PageGroup) => {
    if (!page.columns.length) return;

    const ok = window.confirm(
      `${translate("Delete")} ${page.columns.length} ${translate("Column")} (${getPageLabel(page.id_page, page.page_name)}) ?`
    );
    if (!ok) return;

    try {
      let success = 0;
      for (const col of page.columns) {
        try {
          await removeHiddenColumn({ id_user: userId, id_page: page.id_page, column_name: col });
          success++;
        } catch (e) {
          console.error(e);
        }
      }

      if (success > 0) {
        toast.success(translate("Deleted"), { position: "bottom-right", autoClose: 1500 });
        await getPreferences();
      } else {
        toast.error(translate("Delete failed"), { position: "bottom-right", autoClose: 2500 });
      }
    } catch (e) {
      console.error(e);
      toast.error(translate("Delete failed"), { position: "bottom-right", autoClose: 2500 });
    }
  };

  return (
    <>
      <div className="row">
        <h4 className="col-sm-6 col-md-6">
          <i className="las la-sliders-h"></i> {translate("Preferences")} ({totalPagesRows})
        </h4>
      </div>

      {/* Search bar */}
      <div className="row">
        <div className="col-md-4 list-search-column" style={{ margin: "0px 0px 10px 0px", padding: "10px" }}>
          <ListSearchBar criteria={<div className="dropdown">
              <button className="btn btn-link custom-toggle dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                {filterLabel}
              </button>

              <ul className="dropdown-menu" onClick={handleTypeSearch}>
                {OPTIONS.map((opt) => (
                  <li key={opt.key}>
                    <button
                      className={`dropdown-item ${selectedSearch === opt.label ? "active" : ""}`}
                      type="button"
                      onClick={() => setSelectedSearch(opt.label)}
                    >
                      {opt.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>} onReset={() => { setSearch(""); setCurrentPage(1); }} resetLabel={translate("Reset search")}>
<input
              type="text"
              placeholder={`${translate("By")} ${filterLabel}`}
              onChange={handleAdvancedSearch}
              className="form-control"
              value={search}
            />
</ListSearchBar>
        </div>

        <div className="col-md-8 d-flex justify-content-end align-items-center">
          <div className="dataTables_length">
            <label style={{ marginBottom: 0 }}>
              {translate("Show")}
              <select
                className="custom-select custom-select-sm form-control form-control-sm ml-2"
                style={{ width: "66px" }}
                onChange={handleSelectChange}
                value={limitValue}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="row">
        <div className="table-responsive">
          <Table className="dataTable">
            <thead className="bg-white text-uppercase">
              <tr className="ligth ligth-data">
                <th className="sorting" onClick={() => handleSortingColum("user_name")}>
                  {translate("User")}
                </th>
                <th className="sorting" onClick={() => handleSortingColum("page_name")}>
                  {translate("Page")}
                </th>
                <th className="sorting" onClick={() => handleSortingColum("column_name")}>
                  {translate("Column")}
                </th>
                <th className="sorting" onClick={() => handleSortingColum("updated_at")}>
                  {translate("Last update")}
                </th>
                <th>{translate("Actions")}</th>
              </tr>
            </thead>

            <tbody className="ligth-body">
              {loading ? (
                <tr style={{ textAlign: "center" }}>
                  <td className="text-center" colSpan={5}>
                    <PropagateLoader color={"#123abc"} loading={loading} size={20} />
                  </td>
                </tr>
              ) : usersPage.length > 0 ? (
                usersPage.map((u) =>
                  u.pages.map((p, idx) => (
                    <tr key={`${u.id_user}-${p.id_page}`}>
                      {/* ✅ show user only once using rowspan */}
                      {idx === 0 && (
                        <td rowSpan={u.pages.length} style={{ verticalAlign: "top", fontWeight: 600 }}>
                          {u.user_name ?? u.id_user}
                        </td>
                      )}

                      <td>{getPageLabel(p.id_page, p.page_name)}</td>

                      {/* pills */}
                      <td style={{ minWidth: 520 }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                          {p.columns.map((colKey) => (
                            <div
                              key={colKey}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                border: "1px solid #e5e5e5",
                                borderRadius: 999,
                                padding: "6px 10px",
                                background: "#fff",
                              }}
                            >
                              <span style={{ fontWeight: 600 }}>{getColumnLabel(p.id_page, colKey)}</span>

                              <button
                                className="btn btn-sm btn-outline-success"
                                style={{ padding: "2px 10px", borderRadius: 999 }}
                                onClick={() => handleUnhide(u.id_user, p.id_page, colKey)}
                              >
                                {translate("Show")}
                              </button>
                            </div>
                          ))}

                          {p.columns.length === 0 && (
                            <span style={{ color: "#999" }}>{translate("No hidden columns")}</span>
                          )}
                        </div>
                      </td>

                     <td>{formatDateFR(p.updated_at)}</td>

                      <td>
                        <a
                          className="badge bg-danger"
                          style={{ cursor: "pointer" }}
                          title={translate("Delete")}
                          onClick={() => handleDeletePageLine(u.id_user, p)}
                        >
                          <i className="ri-delete-bin-line"></i>
                        </a>
                      </td>
                    </tr>
                  ))
                )
              ) : (
                <tr>
                  <td colSpan={5}>{translate("No preferences available")}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="row">
        <div className="col-md-6 d-flex align-items-center">
          <span>
            {translate("Displaying")} {usersPage.length} {translate("out of")} {totalUsers}
          </span>
        </div>

        <div className="col-md-6">
          <ReactPaginate
            previousLabel={translate("previous")}
            nextLabel={translate("next")}
            breakLabel={"..."}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            containerClassName={"pagination justify-content-end"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            activeClassName={"active"}
            forcePage={currentPage - 1}
          />
        </div>
      </div>
    </>
  );
}
